import ObjectDataStore from "../scripts/ObjectDataStore";
import ComponentClass from "./ComponentClass";
import {
	ComponentEventMap,
	ComponentEventMapStaticMethods,
	DependencyList,
	isComponentEventStatic
} from "./ComponentManager";
import EventManager from "../components/EventManager";

enum DataStoreKey {
	/* TODO @Michael: If not using.
	rootElement = "re",
	 */
	componentDefinition = "def",
	eventMethods = "evm",
	beforeFirstConstructionMethods = "beforeFirstConstructionMethods"
}

type DataStoreMapping = {
	[DataStoreKey.componentDefinition]: ComponentDefinition<any, any>,
	[DataStoreKey.eventMethods]: Map<keyof ComponentEventMap, Set<string>>,
	[DataStoreKey.beforeFirstConstructionMethods]: (() => void)[],
	[key: string]: any
};

/**
 * This is used by ComponentManager to attach instances of a component to an Element as
 * each Element is processed.
 */
export default class ComponentDefinition<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>> {
	private static readonly dataStore = new ObjectDataStore<DataStoreMapping>();
	private static uidCount = 0;

	/**
	 * Pass either a ComponentClass or an instance of one. Returns `undefined` if the class
	 * is not registered, or if it is neither a ComponentClass or an instance of one.
	 * @param componentClassOrInstance
	 */
	static getComponentDefinition<CE extends Element, CC extends ComponentClass<CE, InstanceType<CC>>>(
		componentClassOrInstance: CC | {} | undefined
	): ComponentDefinition<CE, CC> | undefined {
		if (typeof componentClassOrInstance === "object") {
			return componentClassOrInstance.constructor !== undefined
				? ComponentDefinition.dataStore.get(componentClassOrInstance.constructor, DataStoreKey.componentDefinition)
				: undefined;
		} else if (typeof componentClassOrInstance === "function") {
			return ComponentDefinition.dataStore.get(componentClassOrInstance, DataStoreKey.componentDefinition);
		}
		return; // Passed object is likely a primitive data type or is undefined
	}
/* TODO @Michael: If not using.
	static getRootElement(componentInstance: object): Element | undefined {
		return ComponentDefinition.dataStore.get(componentInstance, DataStoreKey.rootElement) as Element;
	}
*/
	static getComponentClass<CE extends Element>(componentInstance: object): ComponentClass<CE, any> | undefined {
		return ComponentDefinition.getComponentDefinition(componentInstance)?.getComponentClass();
	}

	private componentDefinitionExtenders: Array<ComponentDefinition<CE, CC>> | undefined;
	private hasNotYetBroadcastBeforeFirstConstructionEvent = true;

	constructor(
		private readonly componentClass: CC,
		private readonly dependencies: DependencyList,
		public readonly typeUUID: string = "instance_" + ComponentDefinition.uidCount++
	) {
		ComponentDefinition.dataStore.set(componentClass, DataStoreKey.componentDefinition, this);

		const extendedComponentDefinition = this.getExtended();
		if (extendedComponentDefinition !== undefined) {
			extendedComponentDefinition.addExtender(this);
			// TODO (Michael) If it is possible to unregister components in the future, it will be important that the bidirectional linking of ComponentDefinitions is cleaned up.
		}
	}

	getComponentClass(): CC {
		return this.componentClass;
	}

	getExtended(): ComponentDefinition<CE, CC> | undefined {
		const extendedClass = Object.getPrototypeOf(this.componentClass);
		return ComponentDefinition.getComponentDefinition(extendedClass);
	}

	private addExtender(componentDefinitionExtender: ComponentDefinition<CE, CC>) {
		if (this.componentDefinitionExtenders === undefined) {
			this.componentDefinitionExtenders = new Array<ComponentDefinition<CE, CC>>();
		}
		this.componentDefinitionExtenders.push(componentDefinitionExtender);
	}

	/**
	 * Returns null if an instance already exists on this element.
	 */
	newInstance(element: CE, eventManagerDelegate: EventManager<ComponentEventMap>): InstanceType<CC> | null {
		if (this.hasInstance(element, true)) {
			return null;
		}

		this.broadcastBeforeFirstConstructionEventIfNecessary(eventManagerDelegate);

		const instance = new this.componentClass(element);
		/* TODO @Michael: If not using.
		ComponentDefinition.dataStore.set(instance, DataStoreKey.rootElement, elem); */
		ComponentDefinition.dataStore.set(element, this.typeUUID, instance);

		// Event: "added"
		ComponentDefinition.callMethodsForEvent("added", this, instance);
		eventManagerDelegate.notifyEventListeners("added", {element, component: instance});

		return instance;
	}

	private broadcastBeforeFirstConstructionEventIfNecessary(eventManagerDelegate: EventManager<ComponentEventMap>) {
		if (this.hasNotYetBroadcastBeforeFirstConstructionEvent) {
			this.hasNotYetBroadcastBeforeFirstConstructionEvent = false;

			for (let i = 0; i < this.dependencies.length; i++){
				ComponentDefinition.getComponentDefinition(this.dependencies[i])?.broadcastBeforeFirstConstructionEventIfNecessary(eventManagerDelegate);
			}

			ComponentDefinition.callMethodsForEvent("beforeFirstConstruction", this);
			eventManagerDelegate.notifyEventListeners("beforeFirstConstruction", {componentClass: this.componentClass, componentDefinition: this});
		}
	}

	/**
	 * Deletes the instance attached to this element for this component.
	 * Returns the destroyed instance if successful, otherwise null. A false could also mean if there was no instance attached to this element for this component too.
	 */
	destroyInstance(element: CE, eventManagerDelegate: EventManager<ComponentEventMap>): InstanceType<CC> | null {
		if (ComponentDefinition.dataStore === undefined) {
			return null;
		}
		const instance = ComponentDefinition.dataStore.remove(element, this.typeUUID);
		if (instance !== undefined && instance !== null) {
			/* TODO @Michael: If not using.
			ComponentDefinition.dataStore.remove(instance, DataStoreKey.rootElement);  */

			// Event: "removed"
			ComponentDefinition.callMethodsForEvent("removed", this, instance);
			eventManagerDelegate.notifyEventListeners("removed", {element, component: instance});

			return instance;
		}

		return null;
	}

	/**
	 * Returns true or false based on if the element has an instances of this component attached to it.
	 */
	hasInstance(elem: CE, includeExtenders: boolean = false): boolean {
		return ComponentDefinition.dataStore !== undefined && (
			ComponentDefinition.dataStore.has(elem, this.typeUUID) ||
			(includeExtenders && this.componentDefinitionExtenders !== undefined
				&& this.componentDefinitionExtenders.findIndex(cde => cde.hasInstance(elem, includeExtenders)) > -1)
		);
	}

	/**
	 * Gets the instance for this component (or extending component) which is attached to this element.
	 * Returns undefined if no value was found for key.
	 */
	getInstance(elem: CE, includeExtenders: boolean = true): InstanceType<CC> | undefined {
		if (ComponentDefinition.dataStore === undefined) {
			return undefined;
		}
		let instance = ComponentDefinition.dataStore.get(elem, this.typeUUID);
		if (instance !== undefined) {
			return instance;
		}
		if (includeExtenders && this.componentDefinitionExtenders !== undefined) {
			for (let i = 0; i < this.componentDefinitionExtenders.length; i++) {
				const instance = this.componentDefinitionExtenders[i]!.getInstance(elem, includeExtenders);
				if (instance !== undefined) {
					return instance;
				}
			}
		}
		return undefined;
	}

	/**
	 * Returns all component instances attached to the Element.
	 * It does not matter which component it is for -- all are returned.
	 * If none are found, this will return an empty array.
	 */
	static getInstances(elem: Element): any[] {
		return ComponentDefinition.dataStore.getAll(elem);
	}

	static addEventMethodName(componentClass: ComponentClass<any, any>, eventType: keyof ComponentEventMap, methodName: string) {
		let methodNames: Set<string> | undefined;
		let eventMethods = ComponentDefinition.dataStore.get(componentClass, DataStoreKey.eventMethods);
		if (eventMethods === undefined) {
			eventMethods = ComponentDefinition.dataStore.set(componentClass, DataStoreKey.eventMethods, new Map());
			methodNames = new Set();
			eventMethods.set(eventType, methodNames);
		} else {
			methodNames = eventMethods.get(eventType);
			if (methodNames === undefined) {
				methodNames = new Set();
				eventMethods.set(eventType, methodNames);
			}
		}
		methodNames.add(methodName);
	}
	static addBeforeFirstConstructionMethod(componentClass: ComponentClass<any, any>, method: () => void) {
		let methods = ComponentDefinition.dataStore.get(componentClass, DataStoreKey.beforeFirstConstructionMethods);
		if (methods === undefined) {
			methods = ComponentDefinition.dataStore.set(componentClass, DataStoreKey.beforeFirstConstructionMethods, []);
		}
		methods.push(method);
	}
	static getBeforeFirstConstructionMethods(componentClass: ComponentClass<any, any>) {
		let methods = ComponentDefinition.dataStore.get(componentClass, DataStoreKey.beforeFirstConstructionMethods);
		if (methods === undefined) {
			return ComponentDefinition.dataStore.set(componentClass, DataStoreKey.beforeFirstConstructionMethods, []);
		}
		return methods;
	}

	static callMethodsForEvent(
		eventType: keyof ComponentEventMapStaticMethods, componentDefinition: ComponentDefinition<any, any>
	): void;
	static callMethodsForEvent(
		eventType: Exclude<keyof ComponentEventMap, keyof ComponentEventMapStaticMethods>, componentDefinition: ComponentDefinition<any, any>, componentInstance: any
	): void;
	static callMethodsForEvent(
		eventType: keyof ComponentEventMap, componentDefinition: ComponentDefinition<any, any>, component?: any
	) {
		const methodNames = ComponentDefinition.getEventMethodNames(eventType, componentDefinition);
		if (methodNames === undefined) {
			return;
		}
		const methodsObj = isComponentEventStatic(eventType) ? componentDefinition.componentClass : component;
		methodNames.forEach(methodName => {
			const method = methodsObj[methodName];
			if (method !== undefined && typeof method === "function") {
				method.call(methodsObj);
			} else {
				throw new Error(
					`Class property "${methodName}" is marked to be ran as part of "${eventType}" event, ` +
					`but it is either undefined or not a function.`
				);
			}
		});
	}

	private static getEventMethodNames<CE extends Element>(
		eventType: keyof ComponentEventMap, componentDefinition: ComponentDefinition<CE, any>
	): Set<string> | undefined {
		const extended = componentDefinition.getExtended();
		const methodNamesFromExtended = extended !== undefined ? ComponentDefinition.getEventMethodNames(eventType, extended) : undefined;

		const methodNames = ComponentDefinition.dataStore.get(componentDefinition.componentClass, DataStoreKey.eventMethods, undefined)?.get(eventType);

		if (methodNamesFromExtended === undefined) {
			return methodNames;
		} else if (methodNames === undefined) {
			return methodNamesFromExtended;
		}

		const returnMethodNames = new Set(methodNamesFromExtended);
		methodNames.forEach((methodName) => {
			returnMethodNames.add(methodName);
		});
		return returnMethodNames;
	}
}
